/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { getMergedSettings, updateSettings, type MergedSettings } from '../lib/api'

interface SettingsContextType {
  settings: MergedSettings | null
  loading: boolean
  error: string | null
  updateSetting: <K extends keyof MergedSettings>(key: K, value: MergedSettings[K]) => Promise<void>
  updateSettings: (updates: Partial<MergedSettings>) => Promise<void>
  refreshSettings: () => Promise<void>
}

const SettingsContext = createContext<SettingsContextType | null>(null)

const DEFAULT_SETTINGS: MergedSettings = {
  theme: 'dark',
  editor_font_size: 14,
  terminal_font_size: 14,
  vnc_resolution: '1920x1080',
  auto_save: true,
  dashboard: {},
  terminal: {},
  vscode: {},
  browser: {},
  claude_code: {},
  tts: { engine: 'gemini', voice: 'Iapetus', prefer_browser_voice: false },
  customized_categories: [],
}

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<MergedSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch settings on mount
  useEffect(() => {
    let mounted = true

    async function loadSettings() {
      try {
        const data = await getMergedSettings()
        if (mounted) {
          setSettings(data)
          setError(null)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load settings')
          // Use defaults on error
          setSettings(DEFAULT_SETTINGS)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadSettings()

    return () => {
      mounted = false
    }
  }, [])

  // Mirror the mobile app-nav-bar preference into localStorage so the
  // deployed-app proxy snippet (services/router/shared/proxy.py) can
  // self-gate without doing a DB lookup on every HTML response. Same
  // origin → same localStorage. Default-on: only write the "disabled"
  // flag when the user has explicitly turned the bar off. The key is
  // still `cc_back_pill_disabled` for backwards compatibility with
  // browsers that wrote it under the previous (back-pill) UI.
  useEffect(() => {
    if (!settings) return
    const enabled = settings.dashboard?.back_pill_show_on_mobile !== false
    try {
      if (enabled) {
        localStorage.removeItem('cc_back_pill_disabled')
      } else {
        localStorage.setItem('cc_back_pill_disabled', '1')
      }
    } catch {
      // Storage disabled / private mode — snippet fails open and shows the bar.
    }
  }, [settings])

  // Update a single setting
  const updateSetting = useCallback(async <K extends keyof MergedSettings>(
    key: K,
    value: MergedSettings[K]
  ) => {
    if (!settings) return

    // Optimistically update local state
    setSettings((prev) => (prev ? { ...prev, [key]: value } : prev))

    try {
      await updateSettings({ [key]: value })
      // Refresh to get the merged result
      const updated = await getMergedSettings()
      setSettings(updated)
      setError(null)
    } catch (err) {
      // Revert on error
      setSettings(settings)
      setError(err instanceof Error ? err.message : 'Failed to update setting')
      throw err
    }
  }, [settings])

  // Update multiple settings at once
  const updateSettingsBatch = useCallback(async (updates: Partial<MergedSettings>) => {
    if (!settings) return

    // Optimistically update local state
    setSettings((prev) => (prev ? { ...prev, ...updates } : prev))

    try {
      await updateSettings(updates)
      // Refresh to get the merged result
      const updated = await getMergedSettings()
      setSettings(updated)
      setError(null)
    } catch (err) {
      // Revert on error
      setSettings(settings)
      setError(err instanceof Error ? err.message : 'Failed to update settings')
      throw err
    }
  }, [settings])

  // Refresh settings from server
  const refreshSettings = useCallback(async () => {
    try {
      const data = await getMergedSettings()
      setSettings(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh settings')
    }
  }, [])

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        updateSetting,
        updateSettings: updateSettingsBatch,
        refreshSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const ctx = useContext(SettingsContext)
  if (!ctx) {
    throw new Error('useSettings must be used within SettingsProvider')
  }
  return ctx
}

// Convenience hook for accessing theme
export function useTheme() {
  const { settings, updateSetting, loading } = useSettings()

  const setTheme = useCallback(async (theme: string) => {
    await updateSetting('theme', theme)
  }, [updateSetting])

  return {
    theme: settings?.theme ?? 'dark',
    setTheme,
    loading,
  }
}
